import { FormControl, FormHelperText, TextField } from "@mui/material";
import { ChangeEvent, FC, useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { useDynamicFormsTranslate } from "@ryde/redux";
import { ICustomField, ValidationHelpers } from "@ryde/shared";

export const JSONEditorTemplate: FC<ICustomField> = ({
  name,
  formLabel,
  formAttributes,
  formValidation,
}) => {
  const {
    control,
    setValue,
    setError,
    clearErrors,
    getValues,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation();
  const { formLabelTranslated } = useDynamicFormsTranslate({
    name,
    fallbackText: formLabel,
  });
  const formValue = getValues()[name];
  const stringifiedJson = useMemo(
    () => JSON.stringify(formValue ?? {}),
    [formValue],
  );
  const [valueToShow, setValueToShow] = useState<string>(stringifiedJson);

  const setJson = (e: ChangeEvent<HTMLInputElement>): void => {
    try {
      setValueToShow(e.target.value);
      const value = JSON.parse(e.target.value);
      setValue(name, value, {
        shouldDirty: true,
        shouldValidate: true,
      });
      clearErrors(name);
    } catch (error) {
      setError(name, { message: t("errors.invalidJSON") });
    }
  };

  useEffect(() => {
    const defaultValue = JSON.stringify(getValues(name));

    if (!defaultValue) {
      setValue(name, {});
    }
  }, [setValue, name, getValues]);

  useEffect(() => {
    setValueToShow(stringifiedJson);
  }, [stringifiedJson]);

  const requiredComputed = useMemo(() => {
    return ValidationHelpers.hasRequiredRule(formValidation);
  }, [formValidation]);

  return (
    <>
      <Controller
        name={name}
        control={control}
        render={({ field: { value, ...fieldRest } }) => (
          <InvisibleTextField value={value || "{}"} {...fieldRest} />
        )}
      />
      <FormControl
        required={requiredComputed}
        data-e2e-template="json-editor-template"
        data-e2e-field-name={name}
        error={!!errors[name]}
      >
        <TextField
          label={formLabelTranslated}
          required={requiredComputed}
          fullWidth
          multiline
          rows={4}
          onChange={setJson}
          value={valueToShow || "{}"}
          data-e2e-template="json-editor-template"
          error={!!errors[name]}
        />
        {errors[name] && (
          <FormHelperText>{errors[name]?.message as string}</FormHelperText>
        )}
      </FormControl>
    </>
  );
};

const InvisibleTextField = styled(TextField)`
  display: none !important;
`;
